import { useEffect, useMemo, useState } from 'react';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import { useFoundationPackCheckout, usePlanPayment } from '../../hooks/usePlanPayment';
import FoundationPackPopup from './FoundationPackPopup';
import PaymentStatusModal from './PaymentStatusModal';
import PricingPlansCarousel from './PricingPlansCarousel';

export default function PricingPlansSection({ plansByCategory, loading }) {
  const { isPlanSubscribed, refetch } = useSubscriptionContext();
  const {
    startPayment,
    payingPlanId,
    paymentError,
    paymentSuccess,
    clearPaymentFeedback,
    paymentStatus,
    paymentMeta,
    statusMessage,
    checkingStatus,
    closePaymentStatus,
    openPaymentWindow,
    checkPaymentStatus,
  } = usePlanPayment({ onSuccess: refetch });

  const tabs = useMemo(() => {
    if (!plansByCategory) return [];
    return Object.values(plansByCategory).map((group) => ({
      id: group.id,
      label: group.label,
    }));
  }, [plansByCategory]);

  const [activeClass, setActiveClass] = useState('');
  const [compactPlans, setCompactPlans] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px) and (max-width: 1279px)');
    const updateCompact = (event) => setCompactPlans(event.matches);
    setCompactPlans(mediaQuery.matches);
    mediaQuery.addEventListener('change', updateCompact);
    return () => mediaQuery.removeEventListener('change', updateCompact);
  }, []);

  useEffect(() => {
    if (tabs.length && !tabs.find((tab) => tab.id === activeClass)) {
      setActiveClass(tabs[0].id);
    }
  }, [tabs, activeClass]);

  const plans = useMemo(() => {
    return plansByCategory?.[activeClass]?.plans || [];
  }, [plansByCategory, activeClass]);

  const {
    open,
    selectedPlanId,
    setSelectedPlanId,
    openPack,
    closePack,
    handleProceed,
  } = useFoundationPackCheckout({
    plans,
    startPayment,
    isPlanSubscribed,
  });

  return (
    <section
      id="pricing-plans"
      className="relative z-10 bg-white px-4 py-10 shadow-[0_8px_40px_rgba(0,0,0,0.12)] sm:px-6 sm:py-12 lg:px-8"
    >
      <div className="relative mx-auto min-w-0 max-w-7xl">
        {(paymentError || paymentSuccess) && !paymentStatus && (
          <div className="mx-auto mb-6 max-w-2xl">
            {paymentError && (
              <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-red-700">
                {paymentError}
                <button type="button" onClick={clearPaymentFeedback} className="ml-2 font-semibold underline">
                  Dismiss
                </button>
              </p>
            )}
            {paymentSuccess && (
              <p className="rounded-lg bg-green-50 px-4 py-3 text-center text-sm text-green-700">
                {paymentSuccess}
                <button type="button" onClick={clearPaymentFeedback} className="ml-2 font-semibold underline">
                  Dismiss
                </button>
              </p>
            )}
          </div>
        )}

        {tabs.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveClass(tab.id)}
                className={`rounded-lg px-5 py-2.5 text-sm font-bold tracking-wide transition sm:px-7 sm:text-base ${
                  activeClass === tab.id
                    ? 'bg-[#1A87CC] text-white shadow-md'
                    : 'bg-[#F3F2F3] text-[#999999] shadow-sm'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <PricingPlansCarousel
          plans={plans}
          loading={loading}
          startPayment={openPack}
          payingPlanId={payingPlanId}
          isPlanSubscribed={isPlanSubscribed}
          compact={compactPlans}
        />
      </div>

      <FoundationPackPopup
        open={open}
        plans={plans}
        selectedPlanId={selectedPlanId}
        onSelectPlan={setSelectedPlanId}
        onClose={closePack}
        onProceed={handleProceed}
        paying={Boolean(payingPlanId)}
        isPlanSubscribed={isPlanSubscribed}
      />

      <PaymentStatusModal
        open={Boolean(paymentStatus)}
        status={paymentStatus}
        planName={paymentMeta?.planName}
        amount={paymentMeta?.amount}
        message={statusMessage}
        paymentUrl={paymentMeta?.paymentUrl}
        onClose={closePaymentStatus}
        onOpenPayment={openPaymentWindow}
        onCheckPaid={checkPaymentStatus}
        checking={checkingStatus}
      />
    </section>
  );
}
